type TimeUnit = "hour" | "minute" | "second";

type Translations = {
  [key in TimeUnit]: {
    singular: string;
    plural: string;
  };
};

type FormatDurationOptions = {
  /**
   * Use long format for units (e.g., "hour" instead of "h")
   */
  longFormat?: boolean;
  /**
   * Custom separator between units (default is " ")
   */
  separator?: string;
  /**
   * Hide units with zero values
   */
  hideZeros?: boolean;
  /**
   * Translations for time units
   */
  translations?: Translations;
  /**
   * Use time format (MM:SS)
   * When true, overrides other formatting options
   */
  timeFormat?: boolean;
};

// Default English translations
const DEFAULT_TRANSLATIONS: Translations = {
  hour: {
    singular: "hour",
    plural: "hours",
  },
  minute: {
    singular: "minute",
    plural: "minutes",
  },
  second: {
    singular: "second",
    plural: "seconds",
  },
};

/**
 * Formats a duration in seconds to a human-readable string.
 *
 * @param duration - The duration in seconds
 * @param options - Formatting options
 * @returns Formatted duration string (e.g., "1h 2m 5s" or "1 hour 2 minutes 5 seconds")
 * @throws {Error} If duration is negative or not a number
 *
 * @example
 * // Basic usage
 * formatDuration(3725) // "1h 2m 5s"
 *
 * @example
 * // With Spanish translations
 * formatDuration(3725, {
 *   longFormat: true,
 *   translations: {
 *     hour: { singular: 'hora', plural: 'horas' },
 *     minute: { singular: 'minuto', plural: 'minutos' },
 *     second: { singular: 'segundo', plural: 'segundos' }
 *   }
 * }) // "1 hora 2 minutos 5 segundos"
 */
export const formatDuration = (
  duration: number,
  options: FormatDurationOptions = {},
): string => {
  // Input validation
  if (typeof duration !== "number") {
    throw new Error("Duration must be a number");
  }
  if (duration < 0) {
    throw new Error("Duration must be a positive number");
  }

  const {
    longFormat = false,
    separator = " ",
    hideZeros = false,
    translations = DEFAULT_TRANSLATIONS,
    timeFormat = false,
  } = options;

  // Handle MM:SS format
  if (timeFormat) {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  // Calculate units
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = Math.floor(duration % 60);

  // Define unit formats
  const units = [
    {
      value: hours,
      short: "h",
      unit: "hour" as TimeUnit,
      show: hours > 0,
    },
    {
      value: minutes,
      short: "m",
      unit: "minute" as TimeUnit,
      show: minutes > 0 || hours > 0,
    },
    {
      value: seconds,
      short: "s",
      unit: "second" as TimeUnit,
      show: true, // Always show seconds
    },
  ];

  // Format the duration string
  const parts = units
    .filter((unit) => unit.show)
    .map((unit) => {
      if (hideZeros && unit.value === 0) {
        return null;
      }

      if (longFormat) {
        const translation = translations[unit.unit];
        const label =
          unit.value === 1 ? translation.singular : translation.plural;
        return `${unit.value} ${label}`;
      }

      return `${unit.value}${unit.short}`;
    })
    .filter((part): part is string => (hideZeros ? part !== null : true));

  // Handle special case for zero duration
  if (duration === 0) {
    return "0s";
  }

  return parts.join(separator);
};
